<script>
	import { onMount } from 'svelte'
	import parsePhoneNumber, { AsYouType, isValidPhoneNumber, getExampleNumber } from 'libphonenumber-js/max'
	import examples from 'libphonenumber-js/mobile/examples'
  import Popover from '@components/shared/popover.svelte'
  import ListManager from '@components/shared/list-manager.svelte'
	import { CircleCheck } from 'lucide-svelte'
	import { countries, countryCodeToFlagEmoji } from '@lib/countries'
	
	let { value = $bindable(""), country = "FR", details = $bindable({}), clearOnCountryChange = true } = $props()
	
	let number = $state("")
  let countryOpen = $state(false)
  let countryFilter = $state("")
  let index = $state(-1)

  let numberInputElement = $state()
  let countryTriggerElement = $state()
  let countryListElement = $state()
  let countryInputElement = $state()
	
	let parsed = $derived(parsePhoneNumber(number, country))
	let formatted = $derived(new AsYouType(country).input(number))
	let national = $derived(parsed?.formatNational() || "")
	let valid = $derived(isValidPhoneNumber(value))
	let invalid = $derived(Boolean(number.length) && !valid)
	let type = $derived(parsed?.getType() || "")
	let placeholder = $derived(getExampleNumber(country, examples)?.formatNational())

  let shownCountries = $derived.by(() => {
    if (!countryFilter) return countries
    return countries.filter(c => c.label.toLowerCase().includes(countryFilter.toLowerCase()))
  })

  let iconClass = $derived(countryOpen ? "fal fa-chevron-up text-[0.6rem]" : "fal fa-chevron-down text-[0.6rem]")

	onMount(() => {
		number = new AsYouType("FR").input(value)
		if (parsed?.country) country = parsed.country
		number = national
		setDetails()
	})
	
	$effect(() => {
		value = parsed?.number || ""
		if (parsed?.country) country = parsed.country
		setDetails()
	})

	function handleInput() {
		number = formatted
	}

	function setDetails() {
		details = {
			...parsed,
			valid,
			type
		}
	}

  function handleKeydown(e) {
    if (e.key === "Escape") countryOpen = false
    // If the trigger button is focused, open the country list
    if (countryTriggerElement?.contains(e.target)) countryOpen = true
  }

  function handleClickOutside(e) {
    if (countryOpen && !countryListElement?.contains(e.target) && !countryTriggerElement?.contains(e.target) && !inputElement?.contains(e.target)) countryOpen = false
  }

  function handleCountrySelect({ item, e }) {
    e?.preventDefault()
    e?.stopPropagation()
    country = item.value
    if (clearOnCountryChange) number = ""
    countryOpen = false
    numberInputElement?.focus()
    numberInputElement?.select()
    countryFilter = ""
  }

  function handleCountryEscape() {
    countryOpen = false
    countryFilter = ""
    numberInputElement?.focus()
    numberInputElement?.select()
  }

  $effect(() => {
    if (countryOpen) {
      countryInputElement?.focus()
    }
  })
  
</script>

<svelte:window onkeydown={handleKeydown} onclick={handleClickOutside} />

<div class="flex items-center border border-gray-200 text-[12px] rounded relative focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
  
  <Popover bind:open={countryOpen} placement="bottom-start">
    
    {#snippet trigger()}
      <button type="button" onclick={() => countryOpen = !countryOpen} tabindex="-1"
        class="flex items-center gap-2 pl-3 pr-2 h-full" 
        bind:this={countryTriggerElement}>
        <span class="text-[1.2rem]">{country ? countryCodeToFlagEmoji(country) : "🇫🇷"}</span>
        <i class={iconClass}></i>
      </button>
    {/snippet}

    {#snippet contents({ maxHeight })}
    
      {@const preferredMaxHeight = Math.min(maxHeight, 300) }
      
      <div class="border border-gray-100 shadow-2xl rounded-md bg-white z-100 w-fit overflow-hidden" 
        style="max-height: {preferredMaxHeight}px; width: 20rem;">
        
        <ListManager bind:index bind:items={shownCountries}
          onSelect={handleCountrySelect}
          onEscape={handleCountryEscape}>

          {#snippet input(handlers)}
            <input
              type="search"
              placeholder="Rechercher un pays"
              bind:this={countryInputElement}
              bind:value={countryFilter}
              class="w-full p-[10px] h-[40px] focus:outline-none focus:ring-0 focus-within:outline-none focus-within:ring-0 focus:border-none focus:shadow-none"
              onfocus={e => e.target.select()}
              onblur={() => countryOpen = false}
              {...handlers}
            />
          {/snippet}

          <div class="overflow-y-scroll" style="max-height: {preferredMaxHeight - 50}px;" bind:this={countryListElement}>
            {#each shownCountries as c, idx (c.value)}
              <button type="button" class="menu-item px-3 py-2 text-sm items-center gap-2"
                class:highlighted={idx === index}
                onpointerdown={(e) => handleCountrySelect({ item: c, e })}
              >
                <span class="text-[1.2rem]">{countryCodeToFlagEmoji(c.value)}</span>
                <span>{c.label}</span>
                {#if c.dialCode}
                  <span class="text-gray-400 text-xs code">(+{c.dialCode})</span>
                {/if}
              </button>
            {/each}
          </div>
          
        </ListManager>
      
      </div>

    {/snippet}
    
  </Popover>
	
	<input 
		bind:value={number} 
		oninput={handleInput}
    onfocus={e => e.target.select()}
		{placeholder}
		class="flex-1 w-full p-0 !rounded-none border-none focus:outline-none focus:ring-0" bind:this={numberInputElement} />

	{#if valid}
    <CircleCheck class="text-green-600 absolute right-2" size={14} strokeWidth={1.5} />
	{/if}
	
</div>

<style>
	.valid {
		border-color: green;
	}
	.invalid {
		border-color: red;
	}
  .highlighted {
    background-color: dodgerblue;
    color: white;
    .code {
      color: white;
    }
  }
  
  /* Ensure no focus styles on the country filter input */
  input[type="search"]:focus {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
    background-color: transparent !important;
  }
  
  input[type="search"]:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
</style>